import { ENTITY_TYPES, EntityType } from './entity-type';

export const PRESENTATION_ACCESS = {
  EDITOR: 'presentation:editor',
  PUBLISHER: 'presentation:publisher',
  FULL_ACCESS: 'presentation:fullAccess',
} as const;

export type PresentationAccess =
  (typeof PRESENTATION_ACCESS)[keyof typeof PRESENTATION_ACCESS];

export const SCHEDULE_ACCESS = {
  PLAYLIST_MANAGER: 'schedule:playlistManager',
  FULL_ACCESS: 'schedule:fullAccess',
} as const;

export type ScheduleAccess =
  (typeof SCHEDULE_ACCESS)[keyof typeof SCHEDULE_ACCESS];

export const DISPLAY_ACCESS = {
  SCREEN_SHARING_MODERATOR: 'display:screenSharingModerator',
  FULL_ACCESS: 'display:fullAccess',
} as const;

export type DisplayAccess =
  (typeof DISPLAY_ACCESS)[keyof typeof DISPLAY_ACCESS];

export type AccessLevel = PresentationAccess | ScheduleAccess | DisplayAccess;

export interface AccessRequirement {
  requireAccess?: AccessLevel;
  requireRole?: string;
  allowAssignedPublisher?: boolean;
}

const ACCESS_RANK: Record<AccessLevel, number> = {
  [PRESENTATION_ACCESS.EDITOR]: 1,
  [PRESENTATION_ACCESS.PUBLISHER]: 2,
  [PRESENTATION_ACCESS.FULL_ACCESS]: 3,
  [SCHEDULE_ACCESS.PLAYLIST_MANAGER]: 1,
  [SCHEDULE_ACCESS.FULL_ACCESS]: 2,
  [DISPLAY_ACCESS.SCREEN_SHARING_MODERATOR]: 1,
  [DISPLAY_ACCESS.FULL_ACCESS]: 2,
};

export function getEntityType(level: AccessLevel): EntityType | null {
  const prefix = level.split(':')[0];

  return (
    (Object.values(ENTITY_TYPES) as readonly EntityType[]).find(
      (entityType) => entityType === prefix,
    ) ?? null
  );
}

export function getAccessLevelRank(level: AccessLevel): number | null {
  return ACCESS_RANK[level] ?? null;
}

export function meetsAccessLevel(
  actual: AccessLevel,
  required: AccessLevel,
): boolean {
  if (getEntityType(actual) !== getEntityType(required)) {
    return false;
  }

  const actualRank = getAccessLevelRank(actual);
  const requiredRank = getAccessLevelRank(required);

  if (actualRank === null || requiredRank === null) {
    return false;
  }

  return actualRank >= requiredRank;
}
